
import React from 'react';
import { Helmet } from 'react-helmet';
import NavigationBar from '@/components/landing/NavigationBar';
import Footer from '@/components/landing/Footer';
import Heading from '@/components/ui/heading';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Link } from 'react-router-dom';

const BlogPage = () => {
  const blogPosts = [
    {
      id: 1,
      title: "AI in Market Research: The Revolution Is Here",
      excerpt: "How artificial intelligence is transforming traditional market research methodologies and democratizing access to insights.",
      author: "Sarah Chen",
      date: "May 15, 2023",
      category: "AI Trends",
      readTime: "6 min read",
      image: "https://images.unsplash.com/photo-1591696205602-2f950c417cb9?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80"
    },
    {
      id: 2,
      title: "The True Cost of Market Intelligence: Traditional vs. AI-Powered",
      excerpt: "Breaking down the economics of market research and why AI-powered solutions offer better ROI for businesses of all sizes.",
      author: "Thomas Weber",
      date: "April 22, 2023",
      category: "Business Strategy",
      readTime: "8 min read",
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80"
    },
    {
      id: 3,
      title: "Case Study: How FinTech Startup Secured $5M Using AnalystX Insights",
      excerpt: "Learn how a fintech startup leveraged our market intelligence to identify an untapped market opportunity and secure Series A funding.",
      author: "Priya Sharma",
      date: "March 10, 2023",
      category: "Case Studies",
      readTime: "5 min read",
      image: "https://images.unsplash.com/photo-1560472355-109703aa3edc?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80"
    },
    {
      id: 4,
      title: "From Data to Decision: The Art of Interpreting Market Reports",
      excerpt: "Best practices for extracting actionable insights from comprehensive market research reports.",
      author: "Miguel Rodriguez",
      date: "February 28, 2023",
      category: "Data Analysis",
      readTime: "7 min read",
      image: "https://images.unsplash.com/photo-1559526324-4b87b5e36e44?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80"
    },
    {
      id: 5,
      title: "Emerging Markets 2023: Opportunities and Challenges",
      excerpt: "An analysis of emerging market trends and how businesses can position themselves for success in these dynamic environments.",
      author: "Amara Nelson",
      date: "January 17, 2023",
      category: "Market Trends",
      readTime: "9 min read",
      image: "https://images.unsplash.com/photo-1569025690938-a00729c9e1f9?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80"
    },
    {
      id: 6,
      title: "The Ethics of AI in Market Research: Navigating the New Frontier",
      excerpt: "Exploring the ethical considerations and best practices in AI-powered market intelligence.",
      author: "David Park",
      date: "December 5, 2022",
      category: "AI Ethics",
      readTime: "10 min read",
      image: "https://images.unsplash.com/photo-1507146153580-69a1fe6d8aa1?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <Helmet>
        <title>Blog | AnalystX</title>
        <meta name="description" content="Insights, trends, and analysis from the AnalystX team on market intelligence, AI, and business strategy." />
      </Helmet>
      
      <NavigationBar />
      
      <main>
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-b from-gray-900 to-gray-800">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <Heading level={1} className="mb-6 text-4xl md:text-5xl">
                AnalystX Blog
              </Heading>
              <p className="text-xl text-gray-300 mb-8">
                Insights, trends, and analysis from our team of market intelligence experts.
              </p>
            </div>
          </div>
        </section>
        
        {/* Blog Posts Grid */}
        <section className="py-16 bg-gray-800">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {blogPosts.map((post) => (
                <Card key={post.id} className="bg-gray-800/50 border-gray-700/50 overflow-hidden hover:shadow-xl transition-all hover:-translate-y-1">
                  <div className="h-48 overflow-hidden">
                    <img 
                      src={post.image} 
                      alt={post.title} 
                      className="w-full h-full object-cover transition-transform hover:scale-105"
                    />
                  </div>
                  <CardHeader className="pb-2">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded-full">
                        {post.category}
                      </span>
                      <span className="text-xs text-gray-400">
                        {post.readTime}
                      </span>
                    </div>
                    <CardTitle className="text-xl text-white hover:text-blue-400 transition-colors">
                      <Link to={`/blog/${post.id}`}>{post.title}</Link>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-300 text-sm mb-4">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-gray-400">
                        By {post.author}
                      </div>
                      <div className="text-sm text-gray-400">
                        {post.date}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
        
        {/* Newsletter Section */}
        <section className="py-16 bg-gradient-to-b from-gray-800 to-gray-900">
          <div className="container mx-auto px-4">
            <div className="max-w-2xl mx-auto bg-gradient-to-br from-blue-900/30 to-blue-800/20 p-8 rounded-xl border border-blue-500/20">
              <div className="text-center">
                <Heading level={2} className="mb-4 text-2xl">
                  Subscribe to Our Newsletter
                </Heading>
                <p className="text-gray-300 mb-6">
                  Get the latest market intelligence insights delivered straight to your inbox.
                </p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <input 
                    type="email" 
                    placeholder="Your email address" 
                    className="rounded-lg border border-gray-700 bg-gray-800/80 px-4 py-2 text-white flex-grow"
                  />
                  <button className="bg-blue-600 hover:bg-blue-500 text-white rounded-lg px-6 py-2 font-medium">
                    Subscribe
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default BlogPage;
